import { Suspense } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/base/ScrollToTop';
import { SEO } from '../../utils/seo';

const AccessPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "おかやま就労支援事業所",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "○○町1-2-3",
      "addressLocality": "岡山市北区",
      "postalCode": "700-0000",
      "addressRegion": "岡山県",
      "addressCountry": "JP"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.6617",
      "longitude": "133.9350"
    },
    "telephone": "+81-86-012-3456",
    "faxNumber": "+81-86-012-3457",
    "url": siteUrl,
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "founder": {
      "@type": "Person",
      "name": "岡山太郎",
      "jobTitle": "代表取締役"
    }
  };

  const companyInfo = [
    { label: '会社名', value: 'おかやま就労支援事業所' },
    { label: '代表取締役', value: '岡山 太郎' },
    { label: '所在地', value: '〒700-0000 岡山県岡山市北区○○町1-2-3' },
    { label: '電話番号', value: '086-012-3456' },
    { label: 'FAX', value: '086-012-3457' },
    { label: '設立', value: '2026年' },
    { label: '従業員数', value: '9名' },
    { label: '営業時間', value: '9:00 - 18:00' },
    { label: '定休日', value: '日曜日・祝日' },
    {
      label: '事業内容',
      value: '就労継続支援A型・B型事業、就労移行支援事業、障害者雇用支援',
    },
    {
      label: '許認可',
      value: '岡山市指定障害福祉サービス事業所（指定番号：3310100000）',
    },
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEO
        title="アクセス・会社概要｜岡山市北区○○町｜おかやま就労支援事業所"
        description="おかやま就労支援事業所へのアクセス方法と会社概要。岡山市北区○○町1-2-3。JR岡山駅から徒歩15分、専用駐車場完備。代表取締役：岡山太郎。営業時間9:00-18:00、日祝休。TEL:086-012-3456。見学随時受付中。"
        keywords="アクセス,会社概要,岡山市北区,地図,駐車場,おかやま就労支援事業所"
        schema={schema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-bg-ivory">
        <Header />
        <main>
          {/* Hero Section */}
          <section className="relative h-96 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <img
                src="https://public.readdy.ai/ai/img_res/1cfb93491101aec91f513abb2f483d1f.jpg"
                alt="アクセス・会社概要"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
            </div>
            <div className="relative z-10 text-center text-white space-y-4">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">
                アクセス・会社概要
              </h1>
              <p className="text-sm text-white/80">トップ &gt; アクセス</p>
            </div>
          </section>

          {/* Map Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-12">
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-text-dark mb-8 text-center">
                  所在地
                </h2>
                <div className="w-full h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64516579!2d133.89!3d34.66!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355408e8b3c2e3e3%3A0x3e3e3e3e3e3e3e3e!2z5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="おかやま就労支援事業所の地図"
                  ></iframe>
                </div>
              </div>

              {/* Access Information */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 mt-16">
                <div className="lg:col-span-3 space-y-6">
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-text-dark">
                    アクセス情報
                  </h3>
                  <div className="space-y-4 text-text-medium">
                    <div className="flex items-start">
                      <i className="ri-map-pin-line text-primary text-2xl mr-4 flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="font-bold text-text-dark mb-1">住所</p>
                        <p>〒700-0000</p>
                        <p>岡山県岡山市北区○○町1-2-3</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-train-line text-primary text-2xl mr-4 flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="font-bold text-text-dark mb-1">電車でお越しの方</p>
                        <ul className="space-y-1">
                          <li>・JR岡山駅から徒歩15分</li>
                          <li>・岡山電鉄「○○町」電停から徒歩3分</li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-bus-line text-primary text-2xl mr-4 flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="font-bold text-text-dark mb-1">バスでお越しの方</p>
                        <ul className="space-y-1">
                          <li>・岡山駅前バスターミナルから「○○行き」乗車</li>
                          <li>・「○○町」バス停下車、徒歩1分</li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <i className="ri-parking-box-line text-primary text-2xl mr-4 flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="font-bold text-text-dark mb-1">お車でお越しの方</p>
                        <p>専用駐車場5台完備（無料）</p>
                        <p className="text-sm mt-1">※満車の場合は近隣のコインパーキングをご利用ください</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl h-80 lg:h-full">
                    <img
                      src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/750b7b7c546c2bdec6ace4dda1623bbf.jpeg"
                      alt="事業所入口"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Company Overview Section */}
          <section className="py-16 sm:py-20 lg:py-24 bg-bg-beige">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-text-dark mb-12 text-center">
                会社概要
              </h2>
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <table className="w-full">
                  <tbody>
                    {companyInfo.map((info, index) => (
                      <tr key={index} className="border-b border-gray-200 last:border-b-0">
                        <td className="bg-gray-50 px-6 py-4 font-bold text-text-dark w-1/3 align-top">
                          {info.label}
                        </td>
                        <td className="px-6 py-4 text-text-medium">{info.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Contact Info */}
              <div className="mt-12 text-center space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="font-serif text-2xl font-bold text-text-dark mb-6">
                    お問い合わせ
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-text-medium mb-2">お電話でのお問い合わせ</p>
                      <a
                        href="tel:0860123456"
                        className="inline-flex items-center text-primary text-3xl font-bold hover:text-primary/70 transition-colors"
                      >
                        <i className="ri-phone-line mr-2"></i>
                        086-012-3456
                      </a>
                    </div>
                    <div>
                      <p className="text-text-medium text-sm">受付時間: 9:00 - 18:00（日祝休）</p>
                    </div>
                    <div className="pt-4">
                      <a
                        href="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                      >
                        <i className="ri-mail-line mr-2"></i>
                        お問い合わせフォームはこちら
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </Suspense>
  );
};

export default AccessPage;
